import { getContentSettings } from '@app/services/content-setup.service'
import { getGeneralSettings } from '@app/services/general-setup.service'
import { getNavigationMenu } from '@app/services/navigation.service'
import { applyCustomColors } from '@app/utils/customization'
import { ContentType } from '@backend-models/content-settings/content-type'
import type { GetGeneralSettingsResponse } from '@backend-models/general-settings/get/get-general-settings-response'
import type { GetUserNavigationMenuItemDto } from '@backend-models/navigation/get-user-navigation-menu/get-user-navigation-menu-item-dto'
import { BehaviorSubject, firstValueFrom } from 'rxjs'

export type Settings =
  | (GetGeneralSettingsResponse & {
      consentEnabled?: boolean
      securityNotice?: string
      securityDistributionStatement?: string
      menu?: GetUserNavigationMenuItemDto[]
    })
  | null

// General settings subject which starts empty
const $settings = new BehaviorSubject<Settings | null>(null)

// General settings observable for subscriptions
const $settingsObservable = $settings.asObservable()

/**
 * Update settings
 * @param settings
 */
export const updateSettings = (settings: Settings) => $settings.next(settings)

/**
 * Get settings observable to listen for settings changes
 * @returns Observable
 */
export const getSettingsObservable = () => $settingsObservable

/**
 * Get current settings
 * @returns Promise
 */
export const getSettings = () => firstValueFrom(getSettingsObservable())

/**
 * Load settings
 * @param settings
 */
export const loadSettings = async () => {
  const { data } = await getGeneralSettings(true)
  const { data: content } = await getContentSettings(true)
  const { data: menu } = await getNavigationMenu(true)
  if (data) {
    const settings: Settings = {
      ...data,
      menu,
      consentEnabled: !!content?.contentSettings.some((setting) => setting.type === ContentType.Consent && setting.isEnabled),
      securityNotice: content?.contentSettings.find((setting) => setting.type === ContentType.SecurityNotice && setting.isEnabled)?.value,
      securityDistributionStatement: content?.contentSettings.find(
        (setting) => setting.type === ContentType.SecurityDistributionStatement && setting.isEnabled,
      )?.value,
    }
    updateSettings(settings)

    // Apply custom colors and scaling if present
    if (settings.colors) {
      applyCustomColors(settings.colors)
    }
  }
}
